import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled"];
import { useFetchContentAssistanceContent } from 'content-assistance-lib/hooks/useFetchContentAssistanceContent';
import { getFullUrl } from 'hubspot-url-utils';
import { BUTTON_DISABLED_TEXT, SLINKY } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLangLocale from 'I18n/utils/getLangLocale';
import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';
import { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import UIMultiLevelDropdown from 'UIComponents/dropdown/UIMultiLevelDropdown';
import UITextToolbarIcon from 'rich-text-lib/components/editor/UITextToolbarIcon';
import UILink from 'UIComponents/link/UILink';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import { AIAssistantOnboardingPopover } from './AIAssistantOnboardingPopover';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import { commands } from './ContentAssistantTypes';
import useSummaryCommand from './useSummaryCommand';
import { InlineAutocompleteMenu } from 'content-assistance-lib/autocomplete/InlineAutocompleteMenu';
import { SLASH_COMMANDS } from '../../plugins/ai-highlight/commands';
import { insertContent } from '../../utils/documentUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledLoadingSpinner = styled(UILoadingSpinner).withConfig({
  displayName: "AIAssistant__StyledLoadingSpinner",
  componentId: "l493rb-0"
})(["height:25px;width:25px;padding:6px 5px;margin:0;"]);
const StyledIcon = styled(UITextToolbarIcon).withConfig({
  displayName: "AIAssistant__StyledIcon",
  componentId: "l493rb-1"
})(["color:", ";"], ({
  disabled
}) => disabled ? BUTTON_DISABLED_TEXT : SLINKY);
const StyledAIContainer = styled.section.withConfig({
  displayName: "AIAssistant__StyledAIContainer",
  componentId: "l493rb-2"
})(["position:absolute;"]);
const AIAssistant = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [aiInfoIconOpen, setAiInfoIconOpen] = useState(false);
  const [popoverActive, setPopoverActive] = useState(false);
  const {
    AI_ASSISTANT: {
      commandsType = 'inbox',
      globalSettingEnabled = false,
      globalSummarizationEnabled = false,
      trackOpenPopover = undefined,
      shouldShowAIOnboardingPopover = false,
      setHasSeenAIOnboardingPopover = () => {}
    } = {}
  } = useContext(EditorConfigContext);
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    isLoading: isGeneratingSummary,
    generateSummary
  } = useSummaryCommand();
  const {
    request
  } = useFetchContentAssistanceContent({
    language: getLangLocale(),
    onError: error => {
      console.warn('Failed to generate AI content');
      Raven.captureException(error);
    },
    onSuccess: response => {
      const tr = editorState.tr.insertText(response[0].text);
      const nextState = editorState.apply(tr);
      setEditorState(nextState);
    }
  });
  const settingsUrl = `${getFullUrl('app')}/settings/${PortalIdParser.get()}/ai-assistants`;
  const toolbarAssistantOptions = [{
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.summarize'),
    onClick: () => generateSummary(),
    disabled: !globalSummarizationEnabled,
    tooltip: !globalSummarizationEnabled ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: 'hubspot-prosemirror.tooltips.customerAnalysisSettingOff_jsx',
      elements: {
        Link: UILink
      },
      options: {
        href: settingsUrl,
        external: true
      }
    }) : null
  }, {
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.write'),
    onClick: () => setPopoverActive(prev => !prev)
  }];
  const isFetchingCaContent = request === 'STARTED';
  const AIAssistantButton = useCallback(_ref => {
    let {
        disabled
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return /*#__PURE__*/_jsx(RTEToolbarButton, Object.assign({}, rest, {
      disabled: disabled,
      "data-test-id": "ai-assistant",
      selected: isDropdownOpen,
      style: {
        position: 'relative',
        minWidth: '24px',
        cursor: 'pointer'
      },
      children: isGeneratingSummary || isFetchingCaContent ? /*#__PURE__*/_jsx(StyledLoadingSpinner, {
        size: "extra-small",
        use: 'primary-white'
      }) : /*#__PURE__*/_jsxs(StyledAIContainer, {
        children: [shouldShowAIOnboardingPopover && /*#__PURE__*/_jsx(AIAssistantOnboardingPopover, {
          aiInfoIconOpen,
          setAiInfoIconOpen,
          setHasSeenAIOnboardingPopover,
          settingsUrl
        }), /*#__PURE__*/_jsx(UITooltip, {
          open: isDropdownOpen ? false : undefined,
          title: globalSettingEnabled ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'hubspot-prosemirror.tooltips.aiAssistant'
          }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "hubspot-prosemirror.tooltips.contentAssistantSettingOff_jsx",
            elements: {
              Link: UILink
            },
            options: {
              href: settingsUrl,
              external: true
            }
          }),
          children: /*#__PURE__*/_jsx(StyledIcon, {
            disabled: disabled,
            name: 'artificialIntelligence'
          })
        })]
      })
    }));
  }, [isDropdownOpen, isGeneratingSummary, isFetchingCaContent, shouldShowAIOnboardingPopover, aiInfoIconOpen, setAiInfoIconOpen, setHasSeenAIOnboardingPopover, settingsUrl, globalSettingEnabled]);
  const showPopover = popoverActive && !isDropdownOpen;
  const onPromptComplete = htmlBody => {
    const nextEditorState = insertContent({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    });
    setEditorState(nextEditorState);
    setPopoverActive(false);
  };
  return /*#__PURE__*/_jsx(UIPopover, {
    open: showPopover,
    use: 'unstyled',
    placement: "bottom",
    content: /*#__PURE__*/_jsx(InlineAutocompleteMenu, {
      currentCommand: SLASH_COMMANDS[commands[commandsType].write],
      onPromptComplete: onPromptComplete,
      onClose: () => {
        setPopoverActive(false);
      },
      trackInteraction: () => {},
      editorContext: '' // set to empty since text in editor is currently not relevant to AI Assistant Toolbar usage
      ,
      usageDescription: 'Inbox toolbar AI Assistant',
      language: getLangLocale()
    }),
    children: /*#__PURE__*/_jsx(UIMultiLevelDropdown, {
      Button: AIAssistantButton,
      buttonUse: 'transparent',
      disabled: !globalSettingEnabled,
      open: isDropdownOpen,
      onOpenChange: e => {
        setIsDropdownOpen(e.target.value);
      },
      onOpenComplete: () => {
        if (trackOpenPopover) {
          trackOpenPopover();
        }
      },
      options: toolbarAssistantOptions
    })
  });
};
export default AIAssistant;